import { defineTour } from '../../types';
import { PERMISSIONS, canAny } from './access';

/**
 * T08 — Finish invoice evidence and find reports (brief §7).
 *
 * For a seat that may read invoices, reports or the audit trail; each part is
 * shown only to the seat that may use it, as the screens gate them (the
 * invoice panel on `invoice.view`, its Record button on `invoice.capture`,
 * Reports on `report.view`, Export on `report.export`, the audit trail on
 * `audit.view`). A seat without one of them simply never sees that part.
 *
 * Invoice capture is evidence — not payment and not tax-invoice issuance.
 * Exports write an audit entry, so the guide ends on Export and never runs it.
 *
 * Copy: `guidance.tours.T08.*` — draft, for native-language review.
 */
const ORDER = {
  routeId: 'tenant.purchase-order',
  explainKey: 'guidance.tours.T08.openOrder',
} as const;

const REPORT = {
  routeId: 'tenant.report',
  explainKey: 'guidance.tours.T08.openReport',
} as const;

export const T08 = defineTour({
  id: 'T08',
  version: 1,
  portal: 'tenant',
  titleKey: 'guidance.tours.T08.title',
  summaryKey: 'guidance.tours.T08.summary',
  eligible: (ctx) =>
    ctx.portal === 'tenant' &&
    canAny(ctx, PERMISSIONS.invoiceView, PERMISSIONS.reportView, PERMISSIONS.auditView),
  pageRoutes: ['tenant.purchase-order', 'tenant.audit', 'tenant.reports', 'tenant.report'],
  steps: [
    {
      id: 't08-invoices',
      anchor: 'tenant.po.invoices',
      needsRecord: ORDER,
      when: (ctx) => ctx.can(PERMISSIONS.invoiceView),
      titleKey: 'guidance.tours.T08.invoices.title',
      bodyKey: 'guidance.tours.T08.invoices.body',
    },
    {
      // Opens the capture form; explained, never pressed.
      id: 't08-record-invoice',
      anchor: 'tenant.po.invoice-record',
      needsRecord: ORDER,
      when: (ctx) => ctx.can(PERMISSIONS.invoiceView) && ctx.can(PERMISSIONS.invoiceCapture),
      titleKey: 'guidance.tours.T08.recordInvoice.title',
      bodyKey: 'guidance.tours.T08.recordInvoice.body',
    },
    {
      id: 't08-completion',
      anchor: 'tenant.po.completion',
      needsRecord: ORDER,
      when: (ctx) => ctx.can(PERMISSIONS.invoiceView),
      titleKey: 'guidance.tours.T08.completion.title',
      bodyKey: 'guidance.tours.T08.completion.body',
    },
    {
      id: 't08-audit',
      anchor: 'tenant.audit.list',
      route: 'tenant.audit',
      when: (ctx) => ctx.can(PERMISSIONS.auditView),
      titleKey: 'guidance.tours.T08.audit.title',
      bodyKey: 'guidance.tours.T08.audit.body',
    },
    {
      // The report rail from 1280px; the hub's list below it (CSS shows one).
      id: 't08-reports',
      anchor: ['tenant.reports.rail', 'tenant.reports.list'],
      route: 'tenant.reports',
      when: (ctx) => ctx.can(PERMISSIONS.reportView),
      titleKey: 'guidance.tours.T08.reports.title',
      bodyKey: 'guidance.tours.T08.reports.body',
    },
    {
      id: 't08-filters',
      anchor: 'tenant.report.filters',
      needsRecord: REPORT,
      when: (ctx) => ctx.can(PERMISSIONS.reportView),
      titleKey: 'guidance.tours.T08.filters.title',
      bodyKey: 'guidance.tours.T08.filters.body',
    },
    {
      // Export writes an audit entry: the guide ends here and never exports.
      id: 't08-export',
      anchor: 'tenant.report.export',
      needsRecord: REPORT,
      when: (ctx) => ctx.can(PERMISSIONS.reportView) && ctx.can(PERMISSIONS.reportExport),
      titleKey: 'guidance.tours.T08.export.title',
      bodyKey: 'guidance.tours.T08.export.body',
      commitNote: true,
    },
  ],
});
